/**
 * Feather-style SVG icon library for Intake components.
 *
 * Extracted from the prototype `compass-intake.jsx:12-51`.
 * Each icon is a React component that accepts an optional `size` prop
 * (defaults to a per-icon sensible value matching the prototype).
 *
 * All icons render as inline SVGs with `fill="none"`, `stroke="currentColor"`,
 * `strokeWidth={2}`, `strokeLinecap="round"`, `strokeLinejoin="round"` — the
 * standard Feather convention.
 */
import type { SVGProps } from "react";

export interface IconProps {
  /** Icon size in pixels. Each icon has its own default. */
  size?: number;
}

/* ---------- internal wrapper ---------- */

interface SProps extends SVGProps<SVGSVGElement> {
  s: number;
  children: React.ReactNode;
}

function S({ s, children, ...rest }: SProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={s}
      height={s}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

/* ---------- icons ---------- */

export function ArrowR({ size = 20 }: IconProps) {
  return (
    <S s={size}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </S>
  );
}

export function ArrowL({ size = 20 }: IconProps) {
  return (
    <S s={size}>
      <path d="M19 12H5M11 19l-7-7 7-7" />
    </S>
  );
}

export function ArrowUpR({ size = 14 }: IconProps) {
  return (
    <S s={size}>
      <path d="M7 17L17 7M8 7h9v9" />
    </S>
  );
}

export function LongArr() {
  return (
    <svg
      viewBox="0 0 20 12"
      style={{ width: 18, height: 11, flex: "0 0 auto" }}
      preserveAspectRatio="none"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 6h13M11 2l5 4-5 4" />
    </svg>
  );
}

export function Search({ size = 18 }: IconProps) {
  return (
    <S s={size}>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </S>
  );
}

export function Grid({ size = 15 }: IconProps) {
  return (
    <S s={size}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </S>
  );
}

export function User({ size = 15 }: IconProps) {
  return (
    <S s={size}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </S>
  );
}

export function Bulb({ size = 15 }: IconProps) {
  return (
    <S s={size}>
      <path d="M9 18h6M10 21.5h4" />
      <path d="M12 2.5a6.5 6.5 0 0 0-3.8 11.8c.6.45 1 1.2 1 1.95V17h5.6v-.75c0-.75.4-1.5 1-1.95A6.5 6.5 0 0 0 12 2.5z" />
    </S>
  );
}

export function Clock({ size = 15 }: IconProps) {
  return (
    <S s={size}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </S>
  );
}

export function Pen({ size = 18 }: IconProps) {
  return (
    <S s={size}>
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4 11.5-11.5z" />
    </S>
  );
}

export function X({ size = 20 }: IconProps) {
  return (
    <S s={size}>
      <path d="M18 6L6 18M6 6l12 12" />
    </S>
  );
}

export function Check({ size = 14 }: IconProps) {
  return (
    <S s={size}>
      <path d="M20 6L9 17l-5-5" />
    </S>
  );
}

export function Book({ size = 18 }: IconProps) {
  return (
    <S s={size}>
      <path d="M4 19V5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2zM4 19a2 2 0 0 0 2 2h13" />
    </S>
  );
}

export function Info({ size = 15 }: IconProps) {
  return (
    <S s={size}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-4M12 8h.01" />
    </S>
  );
}

export function Compass({ size = 14 }: IconProps) {
  return (
    <S s={size}>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.88 16.24 9.88 9.88 16.24 7.76" />
    </S>
  );
}

export function Doc({ size = 15 }: IconProps) {
  return (
    <S s={size}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M8 13h8M8 17h6" />
    </S>
  );
}

export function Alert({ size = 14 }: IconProps) {
  return (
    <S s={size}>
      <path d="M12 3.4l8.6 14.9a1 1 0 0 1-.87 1.5H4.27a1 1 0 0 1-.87-1.5L12 3.4z" />
      <path d="M12 10v3.5M12 16.4v.01" />
    </S>
  );
}

export function ChevR({ size = 16 }: IconProps) {
  return (
    <S s={size}>
      <path d="M9 18l6-6-6-6" />
    </S>
  );
}

export function ChevD({ size = 16 }: IconProps) {
  return (
    <S s={size}>
      <path d="M6 9l6 6 6-6" />
    </S>
  );
}

export function Plus({ size = 16 }: IconProps) {
  return (
    <S s={size}>
      <path d="M12 5v14M5 12h14" />
    </S>
  );
}

export function Globe({ size = 14 }: IconProps) {
  return (
    <S s={size}>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </S>
  );
}

export function FileText({ size = 18 }: IconProps) {
  return (
    <S s={size}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8" />
    </S>
  );
}

export function Box({ size = 16 }: IconProps) {
  return (
    <S s={size}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96L12 12l8.73-5.04M12 22V12" />
    </S>
  );
}

export function Target({ size = 16 }: IconProps) {
  return (
    <S s={size}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </S>
  );
}

/**
 * Elion "E" wordmark glyph — the Elion logo mark used in the AI avatar.
 *
 * Extracted from the prototype `compass-intake.jsx:74` (the inline SVG inside `.cs-av`).
 * Uses `fill="currentColor"` so it adapts to the parent's text color.
 */
export function ElionE({ size = 18 }: IconProps) {
  const h = Math.round((size * 200) / 222);
  return (
    <svg
      width={size}
      height={h}
      viewBox="0 0 222 200"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M32.321 113.998C57.8031 113.998 115.856 113.998 141.151 113.998C141.151 104.536 141.151 95.4798 141.151 86.1739C115.653 86.1739 57.6626 86.1739 32.3523 86.1739C32.3523 67.8274 32.3523 49.7307 32.3523 31.353C62.9557 31.353 126.099 31.353 156.562 31.353C156.562 21.7036 156.562 12.3508 156.562 2.87305C115.247 2.87305 41.3615 2.87305 0 2.87305C0 68.733 0 134.374 0 200C42.5951 200 117.667 200 160.153 200C160.153 190.491 160.153 181.138 160.153 171.489C128.394 171.489 64.0487 171.489 32.3054 171.489C32.321 152.252 32.321 133.328 32.321 113.998Z" />
      <path d="M201.328 0C190.132 0 181.045 9.07175 181.045 20.267C181.295 31.1187 190.023 39.847 200.875 40.0968C212.07 40.3466 221.36 31.4779 221.61 20.267C221.61 9.07175 212.538 0 201.328 0Z" />
    </svg>
  );
}

/* ---------- convenience map ---------- */

/**
 * Lookup table mapping icon names to their component.
 * Useful when you need to resolve an icon by string key at runtime.
 */
export const Ic = {
  arrowR: ArrowR,
  arrowL: ArrowL,
  arrowUpR: ArrowUpR,
  longArr: LongArr,
  search: Search,
  grid: Grid,
  user: User,
  bulb: Bulb,
  clock: Clock,
  pen: Pen,
  x: X,
  check: Check,
  book: Book,
  info: Info,
  compass: Compass,
  doc: Doc,
  alert: Alert,
  chevR: ChevR,
  chevD: ChevD,
  plus: Plus,
  globe: Globe,
  fileText: FileText,
  box: Box,
  target: Target,
  elionE: ElionE,
} as const;

export type IconName = keyof typeof Ic;
